import React, { useEffect, useState } from "react";
import { getNetWorth, saveNetWorthSnapshot } from "../../api/plaidClient";
import NetWorthChart from "./NetWorthChart";
import AllocationChart from "./AllocationChart";
import EmergencyFundCard from "./EmergencyFundCard";
import { formatCurrency, formatPercent, formatSyncTime } from "../../utils/formatters";
import ChartLoader from "../ui/ChartLoader";

export default function SummaryRow({
    netWorthData: propData,
    historyData: propHistory,
    emergencyFundData: propEmergencyFund,
    onEmergencyFundUpdate,
    readOnly = false,
    forceShowEmergencyFund = false
}) {
    const [netWorthData, setNetWorthData] = useState(propData || null);
    const [loading, setLoading] = useState(!propData);
    const [error, setError] = useState(null);
    const [showEmergencyFund, setShowEmergencyFund] = useState(() => {
        if (forceShowEmergencyFund) return true;
        const saved = localStorage.getItem("showEmergencyFund");
        return saved !== null ? JSON.parse(saved) : true;
    });

    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem("showEmergencyFund");
            setShowEmergencyFund(saved !== null ? JSON.parse(saved) : true);
        };
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    useEffect(() => {
        if (propData) {
            setNetWorthData(propData);
            setLoading(false);
            return;
        }

        async function fetchNetWorth() {
            try {
                setLoading(true);
                const data = await getNetWorth();
                setNetWorthData(data);
                await saveNetWorthSnapshot();
            } catch (e) {
                console.error("Error fetching net worth:", e);
                setError("Could not load net worth");
            } finally {
                setLoading(false);
            }
        }

        fetchNetWorth();
    }, [propData]);

    if (loading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="card p-6">
                        <div className="h-4 bg-gray-100 rounded w-24 mb-2 animate-pulse"></div>
                        <ChartLoader height={32} />
                    </div>
                ))}
            </div>
        );
    }

    if (error || !netWorthData) {
        return (
            <div className="card p-6 text-center">
                <p className="text-gray-500">{error || "No data available"}</p>
            </div>
        );
    }

    const {
        netWorth,
        totalAssets,
        totalLiabilities,
        cashBalance,
        investmentBalance,
        creditBalance,
        loanBalance,
        change
    } = netWorthData;

    const hasChange = change && (change.amount !== 0 || change.percentage !== 0);
    const isPositiveChange = change && change.amount >= 0;

    return (
        <div className="space-y-4">
            {/* Main Net Worth Card */}
            <div className="card p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-gray-500">Total net worth</p>
                            {netWorthData.calculatedAt && (
                                <span className="text-xs text-gray-400">
                                    · Synced {formatSyncTime(netWorthData.calculatedAt)}
                                </span>
                            )}
                        </div>
                        <p className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
                            {formatCurrency(netWorth)}
                        </p>
                        {hasChange && (
                            <div className={`flex items-center gap-2 mt-2 text-sm ${isPositiveChange ? "text-success-500" : "text-danger-500"}`}>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    {isPositiveChange ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 17l5-5 5 5M7 12l5-5 5 5" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 7l5 5 5-5M7 12l5 5 5-5" />
                                    )}
                                </svg>
                                <span className="font-medium">{formatCurrency(Math.abs(change.amount))}</span>
                                <span className="text-gray-500">({formatPercent(change.percentage, { signed: true })})</span>
                                <span className="text-gray-500">since {change.period}</span>
                            </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">Balances may take up to 24 hours to reflect pending transactions.</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-gray-500">Assets</p>
                        <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalAssets)}</p>
                        <p className="text-xs text-gray-500 mt-2">Liabilities</p>
                        <p className="text-lg font-semibold text-danger-500">-{formatCurrency(totalLiabilities)}</p>
                    </div>
                </div>
            </div>

            {/* Breakdown Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="card p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                        <svg className="w-3.5 h-3.5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                            <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                        </svg>
                        <p className="text-xs text-gray-500">Cash</p>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(cashBalance)}</p>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                        <svg className="w-3.5 h-3.5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                            <polyline points="16 7 22 7 22 13" />
                        </svg>
                        <p className="text-xs text-gray-500">Investments</p>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(investmentBalance)}</p>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                        <svg className="w-3.5 h-3.5 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                            <rect x="2" y="6" width="20" height="12" rx="2" />
                            <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                        <p className="text-xs text-gray-500">Credit cards</p>
                    </div>
                    <p className={`text-lg font-semibold ${creditBalance > 0 ? "text-danger-500" : "text-gray-900"}`}>
                        {creditBalance > 0 ? "-" : ""}{formatCurrency(creditBalance)}
                    </p>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                        <svg className="w-3.5 h-3.5 text-warning-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                            <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
                        </svg>
                        <p className="text-xs text-gray-500">Loans</p>
                    </div>
                    <p className={`text-lg font-semibold ${loanBalance > 0 ? "text-danger-500" : "text-gray-900"}`}>
                        {loanBalance > 0 ? "-" : ""}{formatCurrency(loanBalance)}
                    </p>
                </div>
            </div>

            {/* Charts and Emergency Fund */}
            <div className={`grid grid-cols-1 ${showEmergencyFund ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-4`}>
                <NetWorthChart historyData={propHistory} />
                <AllocationChart netWorthData={netWorthData} />
                {showEmergencyFund && <EmergencyFundCard emergencyFundData={propEmergencyFund} onUpdate={onEmergencyFundUpdate} readOnly={readOnly} />}
            </div>
        </div>
    );
}
